import { useState, useEffect, FC } from 'react'
import { Employee, EmployeeService } from '../../../services/EmployeeService'
import { AppMarkdownViewer } from '../../atoms/AppMarkdownViewer/AppMarkdownViewer'
import { ConfigService } from '../../../services/ConfigService'

type EmployeeResultSummaryProps = {
  employee: Employee
}

const EmployeeResultSummary: FC<EmployeeResultSummaryProps> = ({ employee }) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [markdownContent, setMarkdownContent] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [openAIEnabled, setOpenAIEnabled] = useState<boolean>(true)

  useEffect(() => {
    ConfigService.getConfig(['features']).then(res => {
      const features = (res.data?.features as Record<string, unknown> | undefined) ?? {}
      setOpenAIEnabled(Boolean(features['openAIEnabled']))
    })
  }, [])

  useEffect(() => {
    setLoading(true)
    setError('')

    if (!openAIEnabled) {
      setError('AI profile summary is disabled')
      setLoading(false)
      return
    }

    EmployeeService.getAIUserProfile(employee.id)
      .then(content => {
        setMarkdownContent(content)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching AI summary:', err)
        setError('Failed to load AI profile summary')
        setLoading(false)
      })
  }, [employee.id, openAIEnabled])

  if (loading) {
    return (
      <div className="mt-8">
        <div className="bg-pale-cyan p-6 rounded-lg border border-orange/30 shadow-sm employee-ai-summary">
          <div className="text-2xl font-bold text-dark-blue mb-4">AI-generated Profile Summary</div>
          {/* Skeleton loader with animated lines */}
          <div className="space-y-4">
            <style>{`
              .skeleton-line {
                background-color: #e5e5e5;
                animation: pulse-opacity 1s ease-in-out infinite;
              }
              @keyframes pulse-opacity {
                0% { opacity: 0.3; }
                50% { opacity: 1; }
                100% { opacity: 0.3; }
              }
            `}</style>
            {/* Title skeleton */}
            <div className="h-6 skeleton-line rounded w-1/3"></div>

            {/* Paragraph skeleton */}
            <div className="space-y-2">
              <div className="h-4 skeleton-line rounded"></div>
              <div className="h-4 skeleton-line rounded"></div>
              <div className="h-4 skeleton-line rounded w-5/6"></div>
            </div>

            {/* Section title skeleton */}
            <div className="h-5 skeleton-line rounded w-1/4 mt-6"></div>

            {/* List items skeleton */}
            <div className="space-y-2 ml-6">
              <div className="h-4 skeleton-line rounded w-4/5"></div>
              <div className="h-4 skeleton-line rounded w-3/4"></div>
              <div className="h-4 skeleton-line rounded w-5/6"></div>
            </div>

            {/* Another section skeleton */}
            <div className="h-5 skeleton-line rounded w-1/4 mt-6"></div>

            {/* More list items skeleton */}
            <div className="space-y-2 ml-6">
              <div className="h-4 skeleton-line rounded w-3/4"></div>
              <div className="h-4 skeleton-line rounded w-4/5"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mt-8">
        <div className="bg-pale-cyan p-6 rounded-lg border border-orange/30 shadow-sm employee-ai-summary">
          {!openAIEnabled ? (
            <div className="flex flex-col items-center gap-3 text-center">
              <p className="text-2xl font-bold text-dark-blue">Our AI is taking a break!</p>
              <p className="text-2xl text-grey max-w-2xl">
                AI features are disabled for this first release. Imagine a brilliant, concise summary right here!
              </p>
              <a className="text-orange font-semibold text-2xl" href="mailto:grapes@theagilemonkeys.com">
                Tell us what you'd expect here at grapes@theagilemonkeys.com
              </a>
            </div>
          ) : (
            <div className="text-error-red text-xl text-center">{error}</div>
          )}
        </div>
      </div>
    )
  }

  if (!markdownContent) {
    return null
  }

  return (
    <div className="mt-8">
      <div className="bg-pale-cyan p-6 rounded-lg border border-orange/30 shadow-sm employee-ai-summary">
        <div className="text-2xl font-bold text-dark-blue mb-4">AI-generated Profile Summary</div>
        <AppMarkdownViewer markdown={markdownContent} className="text-[14px]" />
      </div>
    </div>
  )
}

export default EmployeeResultSummary
